import { useEffect } from "react";
import * as THREE from "three";
import { generateFeatureColorPalette } from "../../utils/featureColors";
import {
  normalizeCollisionGeomKind,
  DEFAULT_COLLISION_GEOM_COLORS_BY_KIND,
} from "../../utils/collisionGeomKinds";
import { logger } from "../../utils/logger";

/** Shared golden-angle palette (Joint Editor / Circle Detection style). */
const RAINBOW_PALETTE = generateFeatureColorPalette(256);

const safeTraverse = (root, visitor) => {
  if (!root || typeof visitor !== "function") return;
  const stack = [root];
  const seen = new Set();
  while (stack.length > 0) {
    const obj = stack.pop();
    if (!obj || seen.has(obj)) continue;
    seen.add(obj);
    try {
      visitor(obj);
    } catch (e) {
      logger.warn("[useCollisionGeomAppearance] Visitor error during traversal:", e);
    }
    const children = obj.children;
    if (Array.isArray(children) && children.length > 0) {
      for (let i = children.length - 1; i >= 0; i--) stack.push(children[i]);
    }
  }
};

/** Walk ancestors for `userData.collisionGeomKind` (primitives tag the group; mesh geoms tag the mesh). */
function collisionGeomKindForMesh(mesh) {
  let cur = mesh;
  while (cur) {
    const raw = cur.userData?.collisionGeomKind;
    if (raw != null && String(raw).length > 0) {
      return normalizeCollisionGeomKind(raw);
    }
    cur = cur.parent;
  }
  return normalizeCollisionGeomKind(null);
}

/** Stable palette index for this collision mesh (primitive groups tag the parent; mesh geoms tag the mesh). */
function collisionGeomPaletteKey(mesh) {
  let cur = mesh;
  while (cur) {
    const u = cur.userData;
    if (u?.geomUuid != null && String(u.geomUuid).length > 0) {
      return `geom:${u.geomUuid}`;
    }
    if (u?.instanceId != null && String(u.instanceId).length > 0) {
      return `inst:${u.instanceId}`;
    }
    cur = cur.parent;
  }
  if (mesh?.uuid) return `id:${mesh.uuid}`;
  return "unknown";
}

function fnv1aHash32(str) {
  let h = 2166136261 >>> 0;
  const s = String(str);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

const applySolidToMaterial = (material, color) => {
  if (!material || !material.color) return;
  if (!material.userData?.isCollisionGeomMaterial) return;
  try {
    material.color.set(color);
    material.needsUpdate = true;
  } catch (e) {
    logger.warn("[useCollisionGeomAppearance] Failed to set collision material color:", e);
  }
};

const applyRainbowToMaterial = (material, hexInt) => {
  if (!material || !material.color) return;
  if (!material.userData?.isCollisionGeomMaterial) return;
  try {
    material.color.setHex(hexInt);
    material.needsUpdate = true;
  } catch (e) {
    logger.warn("[useCollisionGeomAppearance] Failed to set rainbow collision color:", e);
  }
};

/**
 * Keeps collision-geom visualization materials in sync with viewer collision color settings.
 */
export function useCollisionGeomAppearance({
  sceneObjects,
  collisionGeomColor,
  collisionGeomColorMode,
  collisionGeomColorsByKind,
}) {
  useEffect(() => {
    if (collisionGeomColorMode === "rainbow") {
      Object.values(sceneObjects).forEach((obj) => {
        safeTraverse(obj, (child) => {
          if (!child?.isMesh) return;
          const mats = child.material;
          const key = collisionGeomPaletteKey(child);
          const idx = fnv1aHash32(key) % RAINBOW_PALETTE.length;
          const hexInt = RAINBOW_PALETTE[idx];
          if (Array.isArray(mats)) {
            mats.forEach((m) => applyRainbowToMaterial(m, hexInt));
          } else {
            applyRainbowToMaterial(mats, hexInt);
          }
        });
      });
      return;
    }

    if (collisionGeomColorMode === "byKind") {
      const scratch = new THREE.Color();
      Object.values(sceneObjects).forEach((obj) => {
        safeTraverse(obj, (child) => {
          if (!child?.isMesh) return;
          const mats = child.material;
          const kind = collisionGeomKindForMesh(child);
          const hex =
            collisionGeomColorsByKind?.[kind] ||
            DEFAULT_COLLISION_GEOM_COLORS_BY_KIND[kind] ||
            "#ff2222";
          try {
            scratch.setStyle(hex);
          } catch (e) {
            logger.warn("[useCollisionGeomAppearance] Invalid by-kind color:", kind, hex, e);
            return;
          }
          if (Array.isArray(mats)) {
            mats.forEach((m) => applySolidToMaterial(m, scratch));
          } else {
            applySolidToMaterial(mats, scratch);
          }
        });
      });
      return;
    }

    const color = new THREE.Color();
    try {
      color.setStyle(collisionGeomColor);
    } catch (e) {
      logger.warn(
        "[useCollisionGeomAppearance] Invalid collisionGeomColor:",
        collisionGeomColor,
        e
      );
      return;
    }

    Object.values(sceneObjects).forEach((obj) => {
      safeTraverse(obj, (child) => {
        if (!child?.isMesh) return;
        const mats = child.material;
        if (Array.isArray(mats)) {
          mats.forEach((m) => applySolidToMaterial(m, color));
        } else {
          applySolidToMaterial(mats, color);
        }
      });
    });
  }, [collisionGeomColor, collisionGeomColorMode, collisionGeomColorsByKind, sceneObjects]);
}
